/* global rwgps */
import React, { Fragment } from 'react'
import { findDOMNode } from 'react-dom'
import styles from './TRouteBottomPanel.scss'
import { SelectionMenu } from './SelectionMenu'
import { SG_COLLAPSED, SG_NORMAL, SG_EXPANDED, SG_CONTROLS_HEIGHT } from '../TRouteShowPage'
import { UPGRADE_MODAL } from 'features/recumbent/UpgradeModal'
import { SampleGraph } from 'common/SampleGraph'
import { Icon } from 'common/Icon'
import { PillGroup } from 'common/styleguide/PillGroup'
import { featureTypes } from 'common/MapV2/featureTypes'
import { PopoverLinkMenuItem } from 'common/styleguide/PopoverLinkMenu'
import { SAVE_AS_SEGMENT_MODAL } from 'common/modals/SaveAsSegmentModal'
import { SAVE_AS_NEW_ROUTE_MODAL } from 'common/modals/SaveAsNewRouteModal'
import { Distance, Elevation } from 'common/units'
import { showModal } from 'lib/modalUtils'
import * as routeUtils from 'lib/routeUtils'
import * as trackpointUtils from 'lib/trackpointUtils'
import { Msg, fm, commonNouns, commonVerbs, commonMetrics, commonMessages } from 'lib/localization'
import { TrackLocation } from 'data/records'
import _ from 'lodash'
import { Set, OrderedMap } from 'immutable'
import { FormattedMessage as T, injectIntl, defineMessages } from 'react-intl'
import cx from 'classnames'

const allMetrics = intl => OrderedMap({
  ele: fm(intl, commonMetrics.ele),
  grade: fm(intl, commonMetrics.grade),
  hr: fm(intl, commonMetrics.hr),
  cad: fm(intl, commonMetrics.cad),
  watts: _.capitalize(fm(intl, commonMetrics.watts)),
  speed: _.capitalize(fm(intl, commonMetrics.speed)),
  temp: fm(intl, commonMetrics.temp)
})

const messages = defineMessages({
  saveAs: {
    id: 'TRouteBottomPanel.saveAs',
    defaultMessage: 'Save as'
  }
})

// only show ele and grade pills by default, until
// we can run the metrics calc
const defaultAvailableMetrics = intl => allMetrics(intl).filter(
  (v, k) => ['ele', 'grade'].includes(k)
)

// sample_graph.js subtracts 25px from the requested height if it can't find
// a div with the class .sg_bar in the DOM. This constant allows us to correct
// for that.
const SG_HEIGHT_CORRECTION = 7

class _TRouteBottomPanel extends React.PureComponent {
  constructor (props) {
    super(props)

    const rwgpsData = new rwgps.Data(routeUtils.parseRawTrackPoints(props.troute.rawPoints))
    this.state = {
      availableMetrics: defaultAvailableMetrics(props.intl),
      activeMetrics: Set(['ele']),
      parentHeight: null,
      dirtyTRoute: null,
      rwgpsData
    }
    this._refs = [React.createRef()]
    this._debouncedResize = _.debounce(this.onWindowResizeComplete, 500, { trailing: true })
  }

  componentDidMount () {
    // fish the available metrics out of the metrics calculator instance
    // for the sample graph, so we don't have to re-run the full metrics
    // calculation just to figure out what metrics to display

    const metrics = this._refs[0].current._sampleGraph.metricsY()
    this._refs = metrics.map(m => React.createRef())

    window.addEventListener('resize', this._debouncedResize)

    this.setState({
      availableMetrics: allMetrics(this.props.intl).filter((v, k) => metrics.find(m => m._key === k))
    }, () => this.layoutExpand())
  }

  componentDidUpdate (prevProps, prevState) {
    const { troute, collapseState } = this.props
    const { dirtyTRoute, parentHeight } = this.state
    if (
      (prevProps.collapseState !== collapseState) ||
      (prevState.parentHeight !== parentHeight)
    ) {
      if (this.props.collapseState === SG_EXPANDED) {
        const node = findDOMNode(this)
        const parentHeight = node?.parentElement.clientHeight
        const height = (parentHeight - SG_CONTROLS_HEIGHT) / this.state.availableMetrics.size - SG_HEIGHT_CORRECTION
        this.setState({ parentHeight }, () => {
          this._refs.map(r => r.current).forEach(sg => sg._sampleGraph.resize(null, height))
        })
      }
      var resizeEvent = window.document.createEvent('UIEvents')
      resizeEvent.initUIEvent('resize', true, false, window, 0)
      window.dispatchEvent(resizeEvent)
    }

    if (prevState.dirtyTRoute !== dirtyTRoute) {
      const rwgpsData = new rwgps.Data(routeUtils.parseRawTrackPoints(troute.rawPoints))
      this.setState({ rwgpsData })
    }
  }

  componentWillUnmount () {
    this._unmounted = true
    window.removeEventListener('resize', this._debouncedResize)
  }

  onWindowResizeComplete = () => {
    // this check is needed because we debounce the resize listener, so in
    // some cases it can fire *after* the listener is removed in componentWillUnmount
    if (this._unmounted) { return }
    this.layoutExpand()
  }

  layoutExpand = () => {
    const node = findDOMNode(this)
    if (!node) { return }
    const parentHeight = node.parentElement.clientHeight
    console.log('layoutExpand', parentHeight)
    if (parentHeight !== this.state.parentHeight) {
      this.setState({ parentHeight })
    }
  }

  onActiveMetricsChange = (activeMetrics) => {
    if (activeMetrics.size === 0) { return } // don't allow the user to deselect all metrics
    this.setState({ activeMetrics })
  }

  getSGHighlightSlice = () => {
    const { selectedFeature } = this.props
    if (!this.hasHighlightSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  getSGHoverSlice = () => {
    const { hoveredFeature } = this.props
    if (!this.hasHoverSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  getSGHighlightPoint = () => {
    const { hoveredFeature } = this.props
    if (!_.get(hoveredFeature, 'data.highlightPoint')) {
      return undefined
    }
    // make a dummy {index} obj to match the older highlightPoint format
    // from RSPv2
    return {
      index: hoveredFeature.data.highlightPoint.index
    }
  }

  onSGPointHighlighted = (p) => {
    const { troute, onFeatureHovered } = this.props
    if (p === null) { // SG calls this with null sometimes to clear the point
      onFeatureHovered(null)
      return
    }
    const trackPoint = troute.trackPoints[p.index]
    onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: { highlightPoint: { trackPoint, index: p.index } }
    })
  }

  hasHighlightSlice = () => {
    const { selectedFeature } = this.props
    return selectedFeature && selectedFeature.data && selectedFeature.data.highlightSlice
  }

  hasHoverSlice = () => {
    const { hoveredFeature } = this.props
    return hoveredFeature && hoveredFeature.data && hoveredFeature.data.highlightSlice
  }

  // safe-access highlightSlice
  highlightSlice = () => {
    return this.hasHighlightSlice()
      ? this.props.selectedFeature.data.highlightSlice
      : null
  }

  highlightedPoints = (opts = {}) => {
    if (!this.highlightSlice()) {
      return []
    }
    const { troute } = this.props
    const indexes = this.highlightSlice().map(TrackLocation.closestIndex)
    if (opts.raw) {
      return troute.rawPoints.slice(indexes[0], indexes[1])
    }
    return troute.trackPoints.slice(indexes[0], indexes[1])
  }

  onSliceHighlighted = (segment) => {
    this.props.onFeatureSelected({
      type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
      firstI: segment[0].left,
      data: { highlightSlice: segment }
    }, this.props.sgZoomed)
  }

  clearSelectedFeature = () => {
    this.props.onFeatureSelected(null)
  }

  applyRouteTransformationToSlice = (transformFn) => {
    const { troute, updateDirtyTRoute } = this.props
    const { dirtyTRoute } = this.state
    const r = dirtyTRoute || troute
    const indexes = this.highlightSlice().map(TrackLocation.closestIndex)
    const withTransform = transformFn(r, indexes[0], indexes[1])
    updateDirtyTRoute(withTransform)
    this.setState({
      dirtyTRoute: withTransform
    })
    this.clearSelectedFeature()
  }

  trimEnds = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      trackpointUtils.trimEnds
    )
  }

  deleteSelection = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      trackpointUtils.deleteSelection
    )
  }

  flattenElevation = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      this.props.objectType === 'route'
        ? routeUtils.flattenElevation
        : trackpointUtils.flattenTripElevation
    )
  }

  excludeFromMetrics = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      routeUtils.excludeFromMetrics
    )
  }

  includeInMetrics = () => {
    this.applyRouteTransformationToSlice(
      routeUtils.includeInMetrics
    )
  }

  showEditUpsell = () => {
    showModal(UPGRADE_MODAL, { feature: 'rideCleanup' })
    this.clearSelectedFeature() // need this to clear SelectionMenu
  }

  revertDirtyTRoute = () => {
    this.props.revertDirtyTRoute()
    this.setState({ dirtyTRoute: null })
  }

  saveDirtyTRoute = () => {
    const { updateTRoute, revertUnsavedChanges } = this.props
    const { dirtyTRoute } = this.state
    if (!dirtyTRoute) { return }

    updateTRoute(dirtyTRoute)
    this.setState({ dirtyTRoute: null })
    revertUnsavedChanges()
  }

  render () {
    const {
      troute,
      objectType,
      currentUser,
      selectedFeature,
      sgZoomed,
      onZoomClicked,
      height,
      metricUnits,
      collapseState,
      onExpand,
      onCollapse,
      intl
    } = this.props
    const { dirtyTRoute, availableMetrics, activeMetrics, parentHeight, rwgpsData } = this.state

    const hasHighlightSlice = this.hasHighlightSlice()
    const showExcludeFromMetrics = hasHighlightSlice && this.highlightedPoints({ raw: true }).some(p => !p.k) // Some points in the highlighted portion are included in metrics
    const showIncludeInMetrics = hasHighlightSlice && this.highlightedPoints({ raw: true }).some(p => !!p.k) // Some points in the highlighted portion have been excluded from metrics

    const editable = troute.permissions && troute.permissions.canBeModified && !troute.locked
    // basic data for "save as/edit" selection modals
    const modalData = this.hasHighlightSlice() && { troute, objectType, highlightSlice: selectedFeature.data.highlightSlice }
    const upsellEdits = currentUser.accountLevel === 0

    const expanded = collapseState === SG_EXPANDED
    const normal = collapseState === SG_NORMAL
    const collapsed = collapseState === SG_COLLAPSED
    const sgHeight = height - SG_CONTROLS_HEIGHT - SG_HEIGHT_CORRECTION
    const expandGraphHeight = (parentHeight - SG_CONTROLS_HEIGHT) / availableMetrics.size - SG_HEIGHT_CORRECTION

    const highlightSlice = this.getSGHighlightSlice()
    const hoverSlice = this.getSGHoverSlice()

    const rawPoints = (dirtyTRoute || troute).rawPoints

    return (
      <div className={styles.TRouteBottomPanel}>
        <div className={styles.sgControls}>
          {!troute.isStationary && <>
            <Icon className={cx(styles.expandControl, expanded && styles.disabled)} i='circle-expand' size={24} onClick={onExpand} />
            <Icon className={cx(styles.expandControl, collapsed && styles.disabled)} i='circle-collapse' size={24} onClick={onCollapse} />
          </>}
          {normal && <PillGroup
            inline
            options={availableMetrics}
            selected={activeMetrics}
            onChange={this.onActiveMetricsChange} />}
          {(!hasHighlightSlice || expanded) && <span className={styles.overallMetrics}>
            <Distance value={troute.distance} />
            {' · '}
            +<Elevation short value={troute.elevationGain} />
            {' / '}
            -<Elevation short value={troute.elevationLoss} />
          </span>}
          <div className={styles.pullRight}>
            {dirtyTRoute && editable && <span className={styles.savePrompt}>
              <a onClick={this.saveDirtyTRoute}>
                <T id='TRouteBottomPanel.saveChanges' defaultMessage='Save changes' />
              </a> or <a onClick={this.revertDirtyTRoute}>
                <T id='TRouteBottomPanel.revert' defaultMessage='Revert' />
              </a>?
            </span>}
            {this.hasHighlightSlice() && <Fragment>
              <a onClick={() => onZoomClicked()}>
                {sgZoomed
                  ? <Msg m={commonMessages.zoomOut} values={{ zoomable: fm(intl, commonNouns.elevation) }} />
                  : <Msg m={commonMessages.zoomIn} values={{ zoomable: fm(intl, commonNouns.elevation) }} />}
              </a>
              <a className={styles.clearSelection} onClick={this.clearSelectedFeature}>
                <T id='TRouteBottomPanel.clearSelection' defaultMessage='Clear selection' />
              </a>
              {editable && <SelectionMenu disabled={upsellEdits} title={fm(intl, commonVerbs.edit)} intl={intl}>
                {objectType === 'trip' && <>
                  <PopoverLinkMenuItem onClick={this.trimEnds}>
                    <T id='TRouteBottomPanel.trimEnds' defaultMessage='Trim ends' />
                  </PopoverLinkMenuItem>
                  <PopoverLinkMenuItem onClick={this.deleteSelection}>
                    <T id='TRouteBottomPanel.deleteSelection' defaultMessage='Delete selection' />
                  </PopoverLinkMenuItem>
                </>}
                <PopoverLinkMenuItem onClick={this.flattenElevation}>
                  <T id='TRouteBottomPanel.flattenSelection' defaultMessage='Flatten selection' />
                </PopoverLinkMenuItem>
                {showExcludeFromMetrics && <PopoverLinkMenuItem onClick={this.excludeFromMetrics}>
                  <T id='TRouteBottomPanel.exclude' defaultMessage='Exclude from metrics' />
                </PopoverLinkMenuItem>}
                {showIncludeInMetrics && <PopoverLinkMenuItem onClick={this.includeInMetrics}>
                  <T id='TRouteBottomPanel.include' defaultMessage='Include in metrics' />
                </PopoverLinkMenuItem>}
              </SelectionMenu>}
              {currentUser.id && <SelectionMenu title={fm(intl, messages.saveAs)} intl={intl}>
                <PopoverLinkMenuItem onClick={() => showModal(SAVE_AS_SEGMENT_MODAL, modalData)}>
                  <Msg capitalized m={commonNouns.segments} values={{ count: 1 }} />
                </PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={() => showModal(SAVE_AS_NEW_ROUTE_MODAL, modalData)}>
                  <T id='TRouteBottomPanel.newRoute' defaultMessage='A new route' />
                </PopoverLinkMenuItem>
              </SelectionMenu>}
            </Fragment>}
            {!dirtyTRoute && !hasHighlightSlice && !collapsed && <span className={styles.hint}>
              <T id='TRouteBottomPanel.dragGraph' defaultMessage='Drag on graph to zoom/select' />
            </span>}
          </div>
        </div>
        {/* plotOnly is used to prevent the 25px height offset that sample graph assumes when it can't find an .sgBar */}
        {expanded && availableMetrics.keySeq().map((m, idx) => <SampleGraph
          key={m}
          ref={this._refs[idx]}
          plotOnly
          height={expandGraphHeight}
          zoomedIn={sgZoomed}
          dragMode='select'
          segmentFormat='TrackLocation'
          rawPoints={rawPoints}
          rwgpsData={rwgpsData}
          highlightSlice={highlightSlice}
          hoverSlice={hoverSlice}
          onSegmentSelected={this.onSliceHighlighted}
          onSegmentUnselected={this.clearSelectedFeature}
          metricsEnabled={Set([m])}
          metricUnits={metricUnits} />)}
        {!expanded && <SampleGraph
          ref={this._refs[0]}
          plotOnly
          height={sgHeight}
          zoomedIn={sgZoomed}
          dragMode='select'
          segmentFormat='TrackLocation'
          rawPoints={rawPoints}
          rwgpsData={rwgpsData}
          highlightPoint={this.getSGHighlightPoint()}
          highlightSlice={highlightSlice}
          hoverSlice={hoverSlice}
          onSegmentSelected={this.onSliceHighlighted}
          onSegmentUnselected={this.clearSelectedFeature}
          onPointHighlighted={this.onSGPointHighlighted}
          metricsEnabled={activeMetrics}
          metricUnits={metricUnits} />}
      </div>
    )
  }
}

export const TRouteBottomPanel = injectIntl(_TRouteBottomPanel)
